import React, { useRef, useEffect, useState } from 'react';
import { User, Settings, CreditCard, LogOut, Crown, AlertCircle, MessageSquare, BarChart3, Smartphone, Music } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import ConfirmationModal from './ConfirmationModal';
import { useNavigate } from 'react-router-dom';

interface AccountDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSettings: () => void;
  onOpenSubscription: () => void;
  onOpenFeedback: () => void;
}

const AccountDropdown: React.FC<AccountDropdownProps> = ({   isOpen,   onClose,   onOpenSettings,   onOpenSubscription,   onOpenFeedback }) => {  const dropdownRef = useRef<HTMLDivElement>(null);  const { currentUser, userProfile, logout, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Refresh user profile when dropdown opens to show latest video count
  useEffect(() => {
    if (isOpen && currentUser) {
      // console.log('🔄 [AccountDropdown] Refreshing user profile for latest quota info');
      refreshUserProfile();
    }
  }, [isOpen, currentUser, refreshUserProfile]);

  // Note: Outside click handling is managed by the parent Navbar component
  // using data attributes to prevent conflicts

  if (!isOpen) return null;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      toast.success('Signed out successfully');
      onClose();
      setShowLogoutConfirm(false);
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to sign out');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const menuItems = [
    {
      icon: BarChart3,
      label: 'Analytics',
      onClick: () => {
        navigate('/analytics');
        onClose();
      }
    },
    {
      icon: Smartphone,
      label: 'Clip Converter',
      onClick: () => {
        navigate('/converter');
        onClose();
      }
    },
    {
      icon: Music,
      label: 'AI Vocal Separation',
      onClick: () => {
        navigate('/audio');
        onClose();
      }
    },
    {
      icon: Settings,
      label: 'Account Settings',
      onClick: () => {
        onOpenSettings();
        onClose();
      }
    },
    {
      icon: CreditCard,
      label: 'Subscription',
      onClick: () => {
        onOpenSubscription();
        onClose();
      }
    },
    {
      icon: MessageSquare,
      label: 'Send Feedback',
      onClick: () => {
        onOpenFeedback();
        onClose();
      }
    },
    {
      icon: LogOut,
      label: 'Sign Out',
      onClick: () => setShowLogoutConfirm(true)
    }
  ];

  return (
    <div
      ref={dropdownRef}
      data-account-dropdown
      className="absolute top-full right-0 mt-2 w-80 bg-white rounded-2xl shadow-lifted border border-primary-100 animate-fade-down z-50"
    >
      {/* User Info */}
      <div className="p-4 border-b border-primary-100">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-black text-white rounded-md flex items-center justify-center overflow-hidden">
            {currentUser?.photoURL ? (
              <>
                <img 
                  src={currentUser.photoURL} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.nextElementSibling?.classList.remove('hidden');
                  }}
                />
                <div className="hidden w-full h-full flex items-center justify-center text-lg font-medium">
                  {currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : <User size={20} />}
                </div>
              </>
            ) : (
              <div className="text-lg font-medium">
                {currentUser?.displayName ? currentUser.displayName.charAt(0).toUpperCase() : <User size={20} />}
              </div>
            )}
          </div>
          <div className="flex-1">
            <p className="font-medium">{userProfile?.displayName || currentUser?.displayName || 'User'}</p>
            <p className="text-sm text-primary-600">{currentUser?.email}</p>
          </div>
          {userProfile?.plan === 'pro' && (
            <Crown size={16} className="text-red-500" />
          )}
        </div>
      </div>

      {/* Usage Stats */}
      <div className="p-4 border-b border-primary-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Videos this month</span>
          <span className="text-sm">{userProfile?.videosProcessed || 0}/{userProfile?.monthlyLimit || 3}</span>
        </div>
        <div className="w-full bg-primary-100 rounded-lg h-2">
          <div 
            className={`bg-black h-2 transition-all duration-300 ${((userProfile?.videosProcessed || 0) / (userProfile?.monthlyLimit || 3)) * 100 >= 100 ? 'rounded-lg' : 'rounded-l-lg'}`}
            style={{ width: `${((userProfile?.videosProcessed || 0) / (userProfile?.monthlyLimit || 3)) * 100}%` }}
          />
        </div>
        {(userProfile?.videosProcessed || 0) >= (userProfile?.monthlyLimit || 3) && (
          <div className="flex items-center mt-2 text-xs text-red-600">
            <AlertCircle size={12} className="mr-1" />
            Monthly limit reached
          </div>
        )}
      </div>

      {/* Plan Info */}
      <div className="p-4 border-b border-primary-100">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium capitalize">{userProfile?.plan || 'free'} Plan</p>
            <p className="text-xs text-primary-600">
              {(userProfile?.plan || 'free') === 'free' ? 'Limited features' : 'All features included'}
            </p>
          </div>
          {(userProfile?.plan || 'free') === 'free' && (
            <button 
              onClick={() => {
                onOpenSubscription();
                onClose();
              }}
              className="btn-primary text-xs"
            >
              Upgrade
            </button>
          )}
        </div>
      </div>

      {/* Menu Items */}
      <div className="p-2">
        {menuItems.map((item, index) => (
          <div key={index}>
            <button
              onClick={item.onClick}
              className="w-full flex items-center space-x-3 px-3 py-2 text-sm hover:bg-primary-50 rounded-lg transition-colors"
            >
              <item.icon size={16} className="text-primary-600" />
              <span>{item.label}</span>
            </button>
            {/* Add separator after AI Vocal Separation */}
            {index === 2 && (
              <div className="border-t border-primary-100 my-2" />
            )}
          </div>
        ))}
      </div>

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogout}
        title="Sign Out"
        message="Are you sure you want to sign out? You'll need to sign back in to access your account."
        confirmText="Sign Out"
        cancelText="Cancel"
        type="danger"
        icon={<LogOut size={24} />}
        isLoading={isLoggingOut}
      />
    </div>
  );
};

export default AccountDropdown; 